import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getBackendUrl, getApiUrl, logConfig } from '../config/environment';

const AuthContext = createContext();

// Utiliser la configuration dynamique
const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

// Log la config au démarrage (debug)
if (process.env.NODE_ENV === 'development') {
  logConfig();
}

// 🔒 P0 EXECUTION CONTEXT: Intercepteur axios global pour injection du header
axios.interceptors.request.use((config) => {
  // Injecter X-Execution-Context-Id si contexte actif
  if (typeof window !== 'undefined' && window.__currentExecutionContextId) {
    config.headers['X-Execution-Context-Id'] = window.__currentExecutionContextId;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// 🔒 P0 EXECUTION CONTEXT: Intercepteur réponse pour traitement silencieux du 499
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Traitement silencieux des erreurs 499 (contexte invalidé)
    if (error.response?.status === 499) {
      console.log('[axios] ⏹️ Request cancelled - context invalidated');
      // Ne pas afficher d'erreur UI - l'utilisateur a navigué ailleurs
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const initAuth = async () => {
      // Cancel any previous pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        // Fetch user profile to restore user data after refresh
        try {
          const profileResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortControllerRef.current.signal
          });
          setUser({ ...profileResponse.data, token });
        } catch (error) {
          // Ignore abort errors (happens during navigation)
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            return;
          }
          // If token is invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    
    initAuth();
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch user profile to get complete user data
      try {
        const profileResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setUser({ ...profileResponse.data, email, token: access_token });
      } catch (profileError) {
        // Fallback if profile endpoint doesn't exist
        setUser({ email, token: access_token, user_id: email });
      }
      
      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur de connexion';
      
      if (detail === 'EMAIL_NOT_VERIFIED') {
        return { success: false, error: 'EMAIL_NOT_VERIFIED', emailNotVerified: true };
      } else if (status === 500) {
        errorMessage = 'Le serveur rencontre un problème. Veuillez réessayer plus tard.';
      } else if (status === 401) {
        errorMessage = 'Identifiants incorrects';
      } else if (status === 404) {
        errorMessage = 'Service temporairement indisponible';
      } else if (detail) {
        errorMessage = detail;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const signup = async (email, password, name, referralCode = null, licenseId = null) => {
    try {
      const signupData = { email: email.trim().toLowerCase(), password, name };
      if (referralCode) {
        signupData.referral_code = referralCode;
      }
      // Phase B MOA: Support licence entreprise
      if (licenseId) {
        signupData.license_id = licenseId;
      }
      
      await axios.post(`${API}/auth/signup`, signupData);
      // Don't save token — user must verify email first
      return { success: true, needsVerification: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de l\'inscription';
      if (detail === 'EMAIL_FORMAT_INVALID') {
        errorMessage = 'Format d\'adresse email invalide';
      } else if (detail === 'EMAIL_DOMAIN_INVALID') {
        errorMessage = 'Le domaine de cette adresse email n\'existe pas';
      } else if (detail) {
        errorMessage = detail;
      }
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};